import {
    computed,
    defineComponent,
    onMounted,
    ref,
} from "vue";
import CopyIdButton from "../components/CopyIdButton.js";
import ImageFallback from "../components/imageFallback.js";
import {
    DefaultEntityClient,
} from "../core/api/EntityClient.js";
import type { MemberSearchRequest } from "../core/api/EntityClient.js";
import type {
    LogRole,
    LogUser,
} from "../core/types.js";
import { sortProfileRoles } from "./users/profile/profileHelpers.js";
import {
    getMemberAvatar,
    getMemberDisplayName,
    getMemberTag,
} from "../utils.js";

type BotFilter = "all" | "humans" | "bots";

const BotFilters: Array<{ key: BotFilter; icon: string; label: string }> = [
    { key: "all", icon: "groups", label: "Tous" },
    { key: "humans", icon: "person", label: "Humains" },
    { key: "bots", icon: "smart_toy", label: "Bots" },
];

export default defineComponent({
    setup() {
        const roles = ref<LogRole[]>([]);
        const members = ref<LogUser[]>([]);
        const total = ref(0);
        const query = ref("");
        const roleQuery = ref("");
        const roleIds = ref<string[]>([]);
        const excludedRoleIds = ref<string[]>([]);
        const draftRoleIds = ref<string[]>([]);
        const draftExcludedRoleIds = ref<string[]>([]);
        const roleMode = ref<"any" | "all">("any");
        const botFilter = ref<BotFilter>("all");
        const rolePickerOpen = ref(false);
        const loading = ref(false);
        const error = ref<string | null>(null);
        let searchController: AbortController | null = null;

        const sortedRoles = computed(() => sortProfileRoles(roles.value).filter((role) => role.name !== "@everyone"));

        const selectedRoles = computed(() => getRolesByIds(sortedRoles.value, roleIds.value));
        const selectedExcludedRoles = computed(() => getRolesByIds(sortedRoles.value, excludedRoleIds.value));
        const hasExcludedRoles = computed(() => excludedRoleIds.value.length > 0);

        const pickerRoles = computed(() => {
            const normalizedQuery = roleQuery.value.trim().toLowerCase();

            return sortedRoles.value
                .filter((role) => !normalizedQuery || role.name.toLowerCase().includes(normalizedQuery) || role.id.includes(normalizedQuery))
                .slice(0, 120);
        });

        onMounted(async () => {
            roles.value = await DefaultEntityClient.getRoles().catch(() => []);
            void searchMembers();
        });

        function buildSearchPayload(): MemberSearchRequest {
            return {
                query    : query.value.trim(),
                bot      : botFilter.value === "all" ? null : botFilter.value === "bots",
                roleIds  : roleIds.value,
                excludedRoleIds: excludedRoleIds.value,
                roleMode : hasExcludedRoles.value ? "all" : roleMode.value,
                limit    : 120,
            };
        }

        async function searchMembers() {
            searchController?.abort();
            searchController = new AbortController();
            loading.value = true;
            error.value = null;

            try {
                const response = await DefaultEntityClient.searchMembers(buildSearchPayload(), searchController.signal);

                members.value = response.members;
                total.value = response.total;
            } catch (cause) {
                if (searchController.signal.aborted)
                    return;

                error.value = cause instanceof Error ? cause.message : "Recherche impossible.";
            } finally {
                if (!searchController.signal.aborted)
                    loading.value = false;
            }
        }

        function openRolePicker() {
            draftRoleIds.value = [ ...roleIds.value ];
            draftExcludedRoleIds.value = [ ...excludedRoleIds.value ];
            roleQuery.value = "";
            rolePickerOpen.value = true;
        }

        function closeRolePicker() {
            rolePickerOpen.value = false;
            roleQuery.value = "";
        }

        function saveRolePicker() {
            roleIds.value = [ ...draftRoleIds.value ];
            excludedRoleIds.value = [ ...draftExcludedRoleIds.value ];

            if (excludedRoleIds.value.length)
                roleMode.value = "all";

            closeRolePicker();
            void searchMembers();
        }

        function toggleDraftRole(roleId: string, mode: "include" | "exclude") {
            const target = mode === "include" ? draftRoleIds : draftExcludedRoleIds;
            const other = mode === "include" ? draftExcludedRoleIds : draftRoleIds;
            const alreadySelected = target.value.includes(roleId);

            target.value = alreadySelected
                ? target.value.filter((id) => id !== roleId)
                : [ ...target.value, roleId ];
            other.value = other.value.filter((id) => id !== roleId);
        }

        function removeRole(roleId: string, mode: "include" | "exclude") {
            if (mode === "include")
                roleIds.value = roleIds.value.filter((id) => id !== roleId);
            else
                excludedRoleIds.value = excludedRoleIds.value.filter((id) => id !== roleId);

            void searchMembers();
        }

        function clearFilters() {
            query.value = "";
            roleQuery.value = "";
            roleIds.value = [];
            excludedRoleIds.value = [];
            draftRoleIds.value = [];
            draftExcludedRoleIds.value = [];
            roleMode.value = "any";
            botFilter.value = "all";
            rolePickerOpen.value = false;
            void searchMembers();
        }

        return () => (
            <div class="member-directory-page">
                <div class="member-directory-hero">
                    <div>
                        <span class="member-directory-eyebrow">Répertoire serveur</span>
                        <h1>Utilisateurs</h1>
                        <p>Recherche les membres par nom, type de compte et rôles. Le serveur charge le cache complet de la guilde avant de filtrer.</p>
                    </div>

                    <div class="member-directory-metric">
                        <strong>{total.value}</strong>
                        <span>résultat{total.value > 1 ? "s" : ""}</span>
                    </div>
                </div>

                <div class="member-directory-toolbar">
                    <div class="member-directory-search member-directory-main-search">
                        <i class="material-symbols-outlined">search</i>
                        <input
                            value={query.value}
                            placeholder="Filtrer par membre, ID, pseudo ou username"
                            onInput={(event) => query.value = (event.target as HTMLInputElement).value}
                            onKeydown={(event) => {
                                if (event.key === "Enter")
                                    void searchMembers();
                            }}
                        />
                    </div>

                    <div class="member-directory-segmented">
                        {BotFilters.map((entry) => (
                            <button
                                key={entry.key}
                                class={{ active: botFilter.value === entry.key }}
                                onClick={() => {
                                    botFilter.value = entry.key;
                                    void searchMembers();
                                }}
                            >
                                <i class="material-symbols-outlined">{entry.icon}</i>
                                {entry.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div class="member-directory-role-filter">
                    <div class="member-directory-role-filter-header">
                        <span>Rôles à filtrer</span>
                        <div class="member-directory-role-mode">
                            <button disabled={hasExcludedRoles.value} class={{ active: roleMode.value === "any" && !hasExcludedRoles.value }} onClick={() => roleMode.value = "any"}>Au moins un</button>
                            <button class={{ active: roleMode.value === "all" }} onClick={() => roleMode.value = "all"}>Tous</button>
                        </div>
                    </div>

                    <div class="member-directory-selected-roles">
                        {selectedRoles.value.map((role) => (
                            <RoleChip key={role.id} role={role} selected onClick={() => removeRole(role.id, "include")} />
                        ))}
                        {selectedExcludedRoles.value.map((role) => (
                            <RoleChip key={role.id} role={role} selected excluded onClick={() => removeRole(role.id, "exclude")} />
                        ))}
                        <button class="member-directory-add-role" onClick={openRolePicker}>
                            <i class="material-symbols-outlined">add</i>
                            Ajouter un rôle
                        </button>
                    </div>
                </div>

                <div class="member-directory-actions">
                    <button class="member-directory-run" disabled={loading.value} onClick={() => searchMembers()}>
                        <i class="material-symbols-outlined">{loading.value ? "progress_activity" : "manage_search"}</i>
                        {loading.value ? "Recherche..." : "Rechercher"}
                    </button>
                    <button class="member-directory-clear" onClick={clearFilters}>Réinitialiser</button>
                </div>

                <div class="member-directory-results">
                    {error.value && <div class="member-directory-notice">{error.value}</div>}

                    {!error.value && loading.value && (
                        <div class="member-directory-grid">
                            {[0, 1, 2, 3, 4, 5].map((item) => <div key={item} class="member-directory-card member-directory-card-skeleton" />)}
                        </div>
                    )}

                    {!error.value && !loading.value && members.value.length === 0 && (
                        <div class="member-directory-empty">
                            <i class="material-symbols-outlined">person_search</i>
                            <strong>Aucun membre trouvé</strong>
                            <span>Change les critères ou réinitialise les filtres.</span>
                        </div>
                    )}

                    {!error.value && !loading.value && members.value.length > 0 && (
                        <div class="member-directory-grid">
                            {members.value.map((member) => <MemberCard key={member.id} member={member} roles={sortedRoles.value} />)}
                        </div>
                    )}
                </div>

                {rolePickerOpen.value && (
                    <div class="member-directory-modal-backdrop" onClick={closeRolePicker}>
                        <div class="member-directory-role-picker" onClick={(event) => event.stopPropagation()}>
                            <div class="member-directory-role-picker-header">
                                <div>
                                    <strong>Filtrer par rôles</strong>
                                    <span>{draftRoleIds.value.length} inclus · {draftExcludedRoleIds.value.length} exclus</span>
                                </div>
                                <button onClick={closeRolePicker} aria-label="Fermer">
                                    <i class="material-symbols-outlined">close</i>
                                </button>
                            </div>

                            <div class="member-directory-search">
                                <i class="material-symbols-outlined">style</i>
                                <input
                                    value={roleQuery.value}
                                    placeholder="Chercher un rôle"
                                    autofocus
                                    onInput={(event) => roleQuery.value = (event.target as HTMLInputElement).value}
                                />
                            </div>

                            <div class="member-directory-role-picker-list">
                                {pickerRoles.value.map((role) => (
                                    <div key={role.id} class="member-directory-role-picker-entry">
                                        <RoleChip role={role} compact />
                                        <div class="member-directory-role-picker-entry-actions">
                                            <button
                                                class={{ active: draftRoleIds.value.includes(role.id) }}
                                                onClick={() => toggleDraftRole(role.id, "include")}
                                            >
                                                Inclure
                                            </button>
                                            <button
                                                class={{ active: draftExcludedRoleIds.value.includes(role.id), excluded: true }}
                                                onClick={() => toggleDraftRole(role.id, "exclude")}
                                            >
                                                Exclure
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div class="member-directory-role-picker-actions">
                                <button class="member-directory-clear" onClick={() => {
                                    draftRoleIds.value = [];
                                    draftExcludedRoleIds.value = [];
                                }}>Vider</button>
                                <button class="member-directory-run" onClick={saveRolePicker}>Sauvegarder</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    },
});

function getRolesByIds(roles: LogRole[], roleIds: string[]): LogRole[] {
    const roleMap = new Map(roles.map((role) => [ role.id, role ]));

    return roleIds
        .map((roleId) => roleMap.get(roleId))
        .filter((role): role is LogRole => Boolean(role));
}

function MemberCard(props: { member: LogUser; roles: LogRole[] }) {
    const roleMap = new Map(props.roles.map((role) => [ role.id, role ]));
    const memberRoles = getMemberRoleIds(props.member)
        .map((roleId) => roleMap.get(roleId))
        .filter((role): role is LogRole => Boolean(role))
        .slice(0, 4);

    return (
        <div class="member-directory-card">
            <div class="member-directory-card-header">
                <router-link to={`/users/${props.member.id}`} class="member-directory-card-profile">
                    <ImageFallback
                        class="member-directory-avatar"
                        src={getMemberAvatar(props.member as any)}
                        default="https://cdn.discordapp.com/embed/avatars/0.png"
                        alt={getMemberDisplayName(props.member as any)}
                    />

                    <div class="member-directory-card-main">
                        <strong>{getMemberDisplayName(props.member as any)}</strong>
                        <span>{getMemberTag(props.member as any)}</span>
                    </div>
                </router-link>

                <div class="member-directory-card-tools">
                    {isBotMember(props.member) && <span class="member-directory-badge">Bot</span>}
                    <CopyIdButton id={props.member.id} label="Identifiant membre" />
                </div>
            </div>

            <div class="member-directory-card-roles">
                {memberRoles.length > 0
                    ? memberRoles.map((role) => <RoleChip key={role.id} role={role} compact link />)
                    : <span class="member-directory-no-roles">Aucun rôle affichable</span>}
            </div>
        </div>
    );
}

function getMemberRoleIds(member: LogUser): string[] {
    if (!Array.isArray(member.serverRoles))
        return [];

    return member.serverRoles.filter((roleId): roleId is string => typeof roleId === "string");
}

function isBotMember(member: LogUser): boolean {
    return Boolean(typeof member.user === "object" && member.user && "bot" in member.user && member.user.bot);
}

function RoleChip(props: { role: LogRole; selected?: boolean; excluded?: boolean; compact?: boolean; link?: boolean; onClick?: () => void }) {
    const content = (
        <>
            <span style={{ background: getRoleColor(props.role) }} />
            {props.excluded && <small class="material-symbols-outlined">block</small>}
            {props.role.unicodeEmoji && <small>{props.role.unicodeEmoji}</small>}
            <strong>{props.role.name}</strong>
            {props.selected && <i class="material-symbols-outlined">close</i>}
        </>
    );

    if (!props.onClick)
        return props.link
            ? <router-link to={`/roles/${props.role.id}`} class={{ "member-directory-role-chip": true, compact: props.compact }}>{content}</router-link>
            : <span class={{ "member-directory-role-chip": true, compact: props.compact }}>{content}</span>;

    return (
        <button class={{ "member-directory-role-chip": true, selected: props.selected, excluded: props.excluded }} onClick={props.onClick}>
            {content}
        </button>
    );
}

function getRoleColor(role: LogRole): string {
    const primary = role.colors?.primaryColor ?? role.colors?.primary_color ?? role.color;

    if (!primary)
        return "#8b95a5";

    return `#${primary.toString(16).padStart(6, "0")}`;
}
